import { axios } from "@/libraries/axios";
import {
	type ExtensionToMime,
	IMAGE_EXTENSIONS,
	IMAGE_MIME_TYPE,
} from "@/libraries/constant";
import { isFile } from "@/libraries/helper";
import type { PresignPayloadSchema } from "@/schemas/storage";
import type { PresignFileResponse } from "@/types/file";
import Axios from "axios";
import type { AxiosRequestConfig } from "axios";

class FileService {
	async presign(payload: PresignPayloadSchema) {
		try {
			const res = await axios.get<PresignFileResponse>("/presign", {
				params: {
					filename: payload.file_name,
				},
			});
			const { fields, headers, url } = res.data;
			const formData = new FormData();
			for (const key in fields) {
				// @ts-expect-error
				formData.append(key, fields[key]);
			}
			formData.append("file", payload.file);
			await Axios.post(url, formData, {
				headers,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async presigns(payload: PresignPayloadSchema[]) {
		try {
			const promPresigns = await Promise.allSettled(
				payload.map((p) => this.presign(p)),
			);
			const isFullfilled = promPresigns.every((p) => p.status === "fulfilled");
			if (!isFullfilled) {
				const errors = promPresigns
					.filter((p) => p.status === "rejected")
					.map((p) => p.reason);
				return Promise.reject(errors);
			}
			return promPresigns.map((p) => p.value as PresignFileResponse);
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async avatarPresign(payload: PresignPayloadSchema) {
		try {
			const res = await axios.get<PresignFileResponse>("/avatar_presign", {
				params: {
					filename: payload.file_name,
				},
			});
			const { fields, headers, url } = res.data;
			const formData = new FormData();
			for (const key in fields) {
				// @ts-expect-error
				formData.append(key, fields[key]);
			}
			formData.append("file", payload.file);
			await Axios.post(url, formData, {
				headers,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async avatarPublicPresign(payload: PresignPayloadSchema) {
		try {
			const res = await axios.get<PresignFileResponse>(
				"/avatar_public_presign",
				{
					params: {
						filename: payload.file_name,
					},
				},
			);
			const { fields, headers, url } = res.data;
			const formData = new FormData();
			for (const key in fields) {
				// @ts-expect-error
				formData.append(key, fields[key]);
			}
			formData.append("file", payload.file);
			await Axios.post(url, formData, {
				headers,
			});
			return res.data;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async downlodFile(url: string, config?: AxiosRequestConfig) {
		try {
			const res = await Axios.get(url, {
				responseType: "blob",
				...config,
			});
			const blob = new Blob([res.data], { type: res.headers["content-type"] });
			const file = new File([blob], url.split("/").pop() as string, {
				type: res.headers["content-type"],
			});
			return file;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async convertFile(file: File | string, config?: AxiosRequestConfig) {
		try {
			let fileBody = file;
			if (!isFile(file)) {
				fileBody = await this.downlodFile(file, config);
			}
			const formData = new FormData();
			formData.append("file", fileBody);
			const res = await axios.post("/api/v1/convert_files", formData, {
				responseType: "blob",
				...config,
			});
			const fileName = fileBody instanceof File ? fileBody.name : "file";
			const blob = new Blob([res.data], { type: res.headers["content-type"] });
			const convertedFile = new File([blob], `${fileName}.png`, {
				type: res.headers["content-type"],
			});
			return convertedFile;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getExtensionFromMimeType(mimeType: string): string {
		switch (mimeType) {
			case IMAGE_MIME_TYPE.PNG:
				return IMAGE_EXTENSIONS.PNG;
			case IMAGE_MIME_TYPE.JPEG:
				return IMAGE_EXTENSIONS.JPEG;
			case IMAGE_MIME_TYPE.JPG:
				return IMAGE_EXTENSIONS.JPG;
			case IMAGE_MIME_TYPE.GIF:
				return IMAGE_EXTENSIONS.GIF;
			default:
				return ".txt";
		}
	}

	imageDataToFile(imageData: ImageData): Promise<File> {
		return new Promise((resolve) => {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			canvas.width = imageData.width;
			canvas.height = imageData.height;
			ctx?.putImageData(imageData, 0, 0);
			canvas.toBlob((blob) => {
				if (!blob) {
					throw new Error("Failed to convert ImageData to Blob");
				}
				const file = new File([blob], "image.png", { type: "image/png" });
				resolve(file);
			});
		});
	}

	/**
	 * @description Convert string to file
	 * @param str - base64 or data url
	 * @param fileName - file name
	 * @param fileType - file type
	 * @returns
	 *
	 * @example
	 * ```ts
	 * const base64 = "data:image/jpg;base64,....";
	 * const file = await fileService.stringToFile(base64, "image");
	 *
	 * const dataUrl = "data:image/jpg;base64,....";
	 * const file = await fileService.stringToFile(dataUrl, "image");
	 * ```
	 */
	stringToFile(
		str: string,
		fileName: string,
		fileType?: string,
	): Promise<File> {
		return new Promise((resolve, reject) => {
			try {
				const byteString = atob(str.split(",")[1]);
				const arrayBuffer = new ArrayBuffer(byteString.length);
				const uint8Array = new Uint8Array(arrayBuffer);
				for (let i = 0; i < byteString.length; i++) {
					uint8Array[i] = byteString.charCodeAt(i);
				}
				let name = fileName;
				if (!fileName.split(".").length) {
					const fileExtension = this.getExtensionFromMimeType(fileType as any);
					name = `${fileName}${fileExtension}`;
				}
				const blob = new Blob([uint8Array], { type: fileType });
				const file = new File([blob], `${name}`, {
					type: fileType,
				});
				resolve(file);
			} catch (e) {
				reject(e);
			}
		});
	}

	blobToFile(blob: Blob, fileName: string): Promise<File> {
		return new Promise((resolve, reject) => {
			try {
				const file = new File([blob], fileName, {
					type: blob.type,
					lastModified: Date.now(),
				});
				resolve(file);
			} catch (e) {
				reject(e);
			}
		});
	}

	getExtension(src: string) {
		try {
			const url = new URL(src);
			const path = url.pathname;
			const extMatch = path.match(/\.[a-zA-Z0-9]+$/);
			return extMatch
				? (extMatch[0].toLowerCase() as keyof ExtensionToMime)
				: null;
		} catch {
			const extMatch = src.match(/\.[a-zA-Z0-9]+$/);
			return extMatch
				? (extMatch[0].toLowerCase() as keyof ExtensionToMime)
				: null;
		}
	}
}

export const fileService = Object.freeze(new FileService());
